document.addEventListener('DOMContentLoaded', async () => {
  const container = document.querySelector('.space-bg-container');
  if (!container) return;

  // Read settings passed from Liquid
  const settingsData = container.dataset.settings;
  if (!settingsData) return;
  
  const settings = JSON.parse(settingsData);
  
  if (!settings.enable) {
    container.style.display = 'none';
    return;
  }

  // Base fallback colors and density
  let ambientColor = settings.fallback_color || '#1a0b2e';
  let starDensity = parseInt(settings.star_density) || 150;

  // Ensure user hasn't requested reduced motion
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // We only run the heavy particle engine if we are on the homepage or product page, and motion isn't reduced
  const isHomepage = window.location.pathname === '/';
  const isProductPage = window.location.pathname.startsWith('/products/');

  if ((!isHomepage && !isProductPage) || prefersReducedMotion) {
    container.classList.add('static-mode');
    container.style.setProperty('--space-bg-color', ambientColor);
    return;
  }

  let particleDirection = "none";
  let foregroundOpacity = 0.85;

  if (settings.live_data) {
    try {
      // 1. Fetch APOD for Nebula Color
      // Note: Using DEMO_KEY for development. Will rate limit quickly.
      const apodRes = await fetch('https://api.nasa.gov/planetary/apod?api_key=DEMO_KEY');
      if (apodRes.ok) {
        const apodData = await apodRes.json();
        // Since we can't easily extract dominant color without canvas manipulation of cross-origin images,
        // we could just apply a pseudo-random dark nebula color based on today's date or just stick to a subtle glow.
        // For demonstration, let's just make it slightly tinted based on the day.
        // Use colors from Shopify settings instead of hardcoding
        const day = new Date().getDay();
        const defaultColors = ['#1a0b2e', '#0b1a2e', '#2e0b1a', '#0a1d1e', '#1d0a1e', '#1e1d0a', '#100b2e'];
        const colors = settings.daily_colors && settings.daily_colors.length === 7 ? settings.daily_colors : defaultColors;
        ambientColor = colors[day];
      }

      // 2. Fetch Moon Phase for Star Density and Foreground Opacity
      const moonPhase = getMoonPhase(); // returns 0-1 (0/1 = new moon, 0.5 = full moon)
      
      const illumination = 1 - Math.abs((moonPhase - 0.5) * 2); // 0 (new) to 1 (full)
      starDensity = Math.floor(starDensity * (1 - (illumination * 0.5)));

      // Foreground opacity adjusts based on moon phase (never totally hidden)
      // High illumination = lower opacity (0.5), Low illumination = higher opacity (0.9)
      foregroundOpacity = 0.9 - (illumination * 0.4);
      container.style.setProperty('--moon-opacity', foregroundOpacity);

      // 3. Fetch ISS Position (Using HTTPS API to avoid Mixed Content errors)
      const issRes = await fetch('https://api.wheretheiss.at/v1/satellites/25544');
      if (issRes.ok) {
        const issData = await issRes.json();
        const lat = parseFloat(issData.latitude);
        const lon = parseFloat(issData.longitude);
        
        // Map longitude to a 0-100% position across the screen for the glow
        const xPos = ((lon + 180) / 360) * 100;
        container.style.setProperty('--iss-x', `${xPos}%`);
        
        // Determine particle drift direction based on ISS position (roughly left/right)
        particleDirection = lon > 0 ? "right" : "left";
      }

    } catch (e) {
      console.warn("Could not fetch live space data. Using fallbacks.", e);
    }
  }

  // Apply the ambient color to the CSS variable
  container.style.setProperty('--space-bg-color', ambientColor);

  // Initialize tsParticles
  if (typeof tsParticles !== 'undefined') {
    tsParticles.load("tsparticles", {
      background: {
        color: {
          value: "transparent",
        },
      },
      fpsLimit: 60,
      interactivity: {
        events: {
          onHover: {
            enable: true,
            mode: "repulse", // Subtle repulsion when hovering
          },
          resize: true,
        },
        modes: {
          repulse: {
            distance: 100,
            duration: 0.4,
          },
        },
      },
      particles: {
        color: {
          value: ["#ffffff", "#aaaaaa", "#e0f2fe"], // White, dim white, slight blue
        },
        links: {
          enable: false, // We want individual stars, not a web
        },
        move: {
          direction: particleDirection,
          enable: true,
          outModes: {
            default: "out",
          },
          random: true,
          speed: particleDirection === "none" ? 0.1 : 0.3, // Faster drift if moving with ISS
          straight: false,
        },
        number: {
          density: {
            enable: true,
            area: 800,
          },
          value: starDensity, // Driven by our logic
        },
        opacity: {
          animation: {
            enable: true,
            speed: 0.5,
            minimumValue: 0.1,
            sync: false,
          },
          value: { min: 0.1, max: 0.8 },
        },
        shape: {
          type: "circle",
        },
        size: {
          value: { min: 0.5, max: 2 },
        },
      },
      detectRetina: true,
    });
  } else {
    console.warn("tsParticles library not loaded.");
  }
});

// Simple approximate moon phase calculation
function getMoonPhase() {
  const lp = 2551443; 
  const now = new Date();
  const new_moon = new Date(1970, 0, 7, 20, 35, 0);
  const phase = ((now.getTime() - new_moon.getTime()) / 1000) % lp;
  return phase / lp;
}
